import { Alert, Button, Form, Input, Typography } from 'antd';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { messageFor } from '@/lib/api/index.js';
import { space, typography } from '@/design/tokens.js';
import { AuthLayout } from './AuthLayout.js';
import { useVerifyMfa } from './mfa.api.js';

const { Text } = Typography;

export interface MfaChallengePageProps {
  challengeId: string;
  onCancel: () => void;
}

/**
 * The second-factor step of a password sign-in.
 *
 * One field, accepting either a six-digit authenticator code or a backup code.
 * Asking the user to first declare which kind they have would be asking them
 * to classify the thing they are holding before they can use it — the server
 * can work that out.
 *
 * `autoComplete="one-time-code"` lets iOS and Android offer the code straight
 * from the SMS or authenticator, which removes the most error-prone part of
 * this screen: transcribing six digits before they expire.
 */
export function MfaChallengePage({ challengeId, onCancel }: MfaChallengePageProps) {
  const navigate = useNavigate();
  const verify = useVerifyMfa();
  const [error, setError] = useState<string | null>(null);
  const [usingBackup, setUsingBackup] = useState(false);

  const onFinish = async (values: { code: string }) => {
    setError(null);
    try {
      await verify.mutateAsync({ challengeId, code: values.code });
      navigate('/', { replace: true });
    } catch (cause) {
      setError(messageFor(cause));
    }
  };

  return (
    <AuthLayout
      title="One more step"
      subtitle={
        usingBackup
          ? 'Enter one of the backup codes you saved when you set this up.'
          : 'Enter the 6-digit code from your authenticator app.'
      }
      footer={
        <Button type="link" onClick={onCancel}>
          Cancel and sign in again
        </Button>
      }
    >
      {error ? (
        <Alert type="error" showIcon title={error} style={{ marginBottom: space[4] }} />
      ) : null}

      <Form layout="vertical" requiredMark={false} onFinish={(v) => void onFinish(v)}>
        <Form.Item
          name="code"
          label={usingBackup ? 'Backup code' : 'Authenticator code'}
          rules={[{ required: true, message: 'Enter your code' }]}
        >
          <Input
            size="large"
            autoFocus
            // Lets phones offer the code directly, rather than making the user
            // transcribe six digits before they expire.
            autoComplete="one-time-code"
            inputMode={usingBackup ? 'text' : 'numeric'}
            placeholder={usingBackup ? 'ABCD-EFGH' : '123456'}
            style={{
              fontSize: typography.size.xl,
              letterSpacing: usingBackup ? '0.05em' : '0.25em',
              textAlign: 'center',
              fontVariantNumeric: 'tabular-nums',
            }}
          />
        </Form.Item>

        <Button type="primary" htmlType="submit" size="large" block loading={verify.isPending}>
          Verify
        </Button>
      </Form>

      <div style={{ textAlign: 'center', marginTop: space[4] }}>
        <Button
          type="link"
          size="small"
          onClick={() => {
            setUsingBackup((value) => !value);
            setError(null);
          }}
        >
          {usingBackup ? 'Use my authenticator app instead' : 'I do not have my phone'}
        </Button>
        {!usingBackup ? (
          <Text
            type="secondary"
            style={{ display: 'block', fontSize: typography.size.sm, marginTop: space[1] }}
          >
            Lost your device? A backup code will get you in.
          </Text>
        ) : null}
      </div>
    </AuthLayout>
  );
}
